import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IChartData, IChartState, TrendType } from './types';
import { deepClone } from '../../../../../../helpers/utils/deepClone';
import { safeLocalStorage } from '../../../../../../helpers/utils/safeStorage';

const initialChartVisible = () => {
  const storageValue = safeLocalStorage.getItem('isChartVisible');
  if(storageValue){
    return storageValue === 'true';
  }

  return true;
};

const initialAxesVisible = () => {
  const storageValue = safeLocalStorage.getItem('isAxesVisible');
  if(storageValue){
    return storageValue === 'true';
  }

  return false; // По умолчанию оси скрыты
};

const initialState: IChartState = {
  chartType: 'line',
  series: [],
  labels: [],
  cumulativeChart: false,
  isChartVisible:initialChartVisible(),
  isAxesVisible:initialAxesVisible(),
  showTrend: true,
  defaultTrendType: 'linear',
  blocks:{}
};
const initialBlockState: Omit<IChartState, 'blocks'> = {
  chartType: 'line',
  series: [],
  labels: [],
  cumulativeChart: false,
  isChartVisible: true,
  isAxesVisible: false,
  showTrend: true,
  defaultTrendType: 'linear',
};
export const chartSlice = createSlice({
  name: 'chart',
  initialState: initialState,
  reducers: {
    setDashboardsBlocksChartState:(state,action: PayloadAction<{ids:string[],data?:Partial<Omit<IChartState,'blocks'>>}>) => {
      const { ids,data } = action.payload;

      const newBlocksValues:{[key:string]:Omit<IChartState,'blocks'>} = {};
      ids.forEach((el)=>{
        newBlocksValues[el] = initialBlockState;
      });
      if(data){
        newBlocksValues[ids[0]] = { ...newBlocksValues[ids[0]],...data };
      }
      state.blocks = { ...state.blocks,...newBlocksValues };
    },
    setChartVisible:(state) => {
      state.isChartVisible = !state.isChartVisible;
    },
    setChartVisibleValue:(state, action: PayloadAction<boolean>) => {
      state.isChartVisible = action.payload;
    },
    setAxesVisible:(state) => {
      state.isAxesVisible = !state.isAxesVisible;
    },
    setAxesVisibleValue: (state, action: PayloadAction<boolean | { data: boolean; blockId: string }>) => {
      if (typeof action.payload === 'boolean') {
        state.isAxesVisible = action.payload;
      } else {
        const { data, blockId } = action.payload;
        if (state.blocks[blockId]) {
          state.blocks[blockId].isAxesVisible = data;
        }
      }
    },
    /** Глобальный вкл/выкл тренда (перебивает индивидуальные настройки метрик) */
    setShowTrend: (state, action: PayloadAction<boolean | { data: boolean; blockId: string }>) => {
      if (typeof action.payload === 'boolean') {
        state.showTrend = action.payload;
      } else {
        const { data, blockId } = action.payload;
        if (state.blocks[blockId]) {
          state.blocks[blockId].showTrend = data;
        }
      }
    },
    /** Установить тип тренда по умолчанию для всех метрик */
    setDefaultTrendType: (state, action: PayloadAction<TrendType | { data: TrendType; blockId: string }>) => {
      if (typeof action.payload === 'string') {
        state.defaultTrendType = action.payload;
      } else {
        const { data, blockId } = action.payload;
        if (state.blocks[blockId]) {
          state.blocks[blockId].defaultTrendType = data;
        }
      }
    },
    setChartType: (state, action: PayloadAction<string | { data:string,blockId:string }>) => {
      const isString = typeof action.payload !== 'object';
      if(isString){
        state.chartType = action.payload as string;
      }else{
        state.blocks[(action.payload as { data:string,blockId:string }).blockId].chartType = (action.payload as { data:string,blockId:string }).data;
      }
    },
    setSeries: (state, action: PayloadAction<IChartData[] | { data:IChartData[],blockId:string }>) => {
      const isArray = Array.isArray(action.payload);
      const newSeries: IChartData[] = isArray 
        ? deepClone(action.payload as IChartData[]) 
        : deepClone((action.payload as { data:IChartData[],blockId:string }).data);
      try{
        newSeries.forEach((el: any)=>{
          el?.group ? el.group = el?.group.replace(/<span[^>]*>.*?<\/span>/gi, '') : null;
        });
      }catch(err){
        console.error(err);
      }
      if(isArray){
        state.series = newSeries;
      }else{
        state.blocks[(action.payload as { data:IChartData[],blockId:string }).blockId].series = newSeries;
      }
    },
    setLabels: (state, action: PayloadAction<IChartData[] | { data:IChartData[],blockId:string }>) => {
      const isArray = Array.isArray(action.payload);
      if(isArray){
        state.labels = action.payload as IChartData[];
      }else{
        state.blocks[(action.payload as { data:IChartData[],blockId:string }).blockId].labels = (action.payload as { data:IChartData[],blockId:string }).data;
      }
    },
    setCumulativeChart: (state, action: PayloadAction<boolean | { data:boolean,blockId:string }>) => {
      const isString = typeof action.payload !== 'object';
      if(isString){
        state.cumulativeChart = action.payload as boolean;
      }else{
        state.blocks[(action.payload as { data:boolean,blockId:string }).blockId].cumulativeChart = (action.payload as { data:boolean,blockId:string }).data;
      }
    },
    setChartClearState: (state) => {
      state.chartType = 'line';
      state.series = [];
      state.labels = [];
      state.cumulativeChart = false;
      // Сохраняем isChartVisible и isAxesVisible из localStorage
      state.isChartVisible = initialChartVisible();
      state.isAxesVisible = initialAxesVisible();
      state.showTrend = true;
      state.defaultTrendType = 'linear';
      // blocks остаются без изменений
    },
    /**
     * Очищает состояние графика для конкретного блока дашборда.
     * Вызывается при удалении блока для предотвращения утечки памяти.
     */
    clearBlockChartState: (state, action: PayloadAction<string>) => {
      const blockId = action.payload;
      if (state.blocks && state.blocks[blockId]) {
        delete state.blocks[blockId];
      }
    },
  },
});

export const chartSliceActions = chartSlice.actions;
export const chartSliceReducer = chartSlice.reducer;
